<template>
  <b-dropdown v-if="hasAnyOption" no-caret variant="transparent" right>
    <template #button-content>
      <b-icon class="kebab" icon="three-dots"></b-icon>
    </template>
    <b-dropdown-item class="pr-0" v-if="showPin" @click="pinFile">
      <b-img :src="require('@/assets/icons/pin.svg')" class="pin mr-2"></b-img>
      <span>{{ isPinned ? 'Unpin' : 'Pin' }}</span>
    </b-dropdown-item>

    <!-- Edit -->
    <b-dropdown-divider v-if="showEdit && showPin" />
    <b-dropdown-item
      v-if="showEdit"
      class="p-0"
      @click="editFile"
      :disabled="!canEditFile"
    >
      <b-icon icon="pencil-square" font-scale="0.95" class="mr-3"></b-icon>
      <span>Edit</span>
    </b-dropdown-item>

    <!-- Download -->
    <b-dropdown-divider v-if="showDownload && (showPin || showEdit)" />
    <b-dropdown-item v-if="showDownload" class="p-0" @click="downloadFile">
      <b-icon icon="download" font-scale="0.95" class="mr-3"></b-icon>
      <span>Download</span>
    </b-dropdown-item>

    <!-- Delete -->
    <b-dropdown-divider
      v-if="showDelete && (showPin || showEdit || showDownload)"
    />
    <b-dropdown-item
      v-if="showDelete"
      class="p-0"
      @click="showDelFiles"
      :disabled="!canDeleteFile"
    >
      <b-icon icon="trash" font-scale="0.95" class="mr-3"></b-icon>
      <span>Delete</span>
    </b-dropdown-item>

    <!-- Share -->
    <b-dropdown-divider
      v-if="showShare && (showPin || showEdit || showDownload || showDelete)"
    />
    <b-dropdown-item v-if="showShare" class="p-0" @click="shareFile">
      <b-icon icon="cursor" font-scale="0.95" class="mr-3"></b-icon>
      <span>Share</span>
    </b-dropdown-item>

    <!-- Cancel Share -->
    <b-dropdown-divider
      v-if="
        showCancelShare &&
        (showPin || showEdit || showDownload || showDelete || showShare)
      "
    />
    <b-dropdown-item
      v-if="showCancelShare"
      class="p-0"
      @click="showCancelShareConfirm"
    >
      <b-icon icon="x-circle" font-scale="0.95" class="mr-3"></b-icon>
      <span>Cancel Share</span>
    </b-dropdown-item>

    <!-- Modal - Delete File -->
    <b-modal
      centered
      v-model="delModalShow"
      id="delete-file-modal"
      modal-class="file-delete-modal"
      :title="
        sharedWithCompanies.length
          ? 'Delete this file?'
          : 'Delete selected file?'
      "
    >
      <div
        v-if="sharedWithCompanies.length"
        class="shared-delete"
        data-testid="shared-delete-body"
      >
        <p class="shared-delete__text">
          This file has been shared with
          <span
            v-for="(company, i) in sharedWithCompanies"
            :key="company.id"
            class="shared-delete__item"
          >
            <b-badge pill class="shared-delete__company">{{
              company.name
            }}</b-badge
            >{{ i === sharedWithCompanies.length - 1 ? '' : ',' }}
          </span>
        </p>
        <p class="shared-delete__text">
          If you delete it, the company will be notified and given 1 week to
          review the file.
        </p>
        <p class="shared-delete__text shared-delete__text--muted">
          The company may retain a copy or record where required or permitted
          for employment, compliance, safety or legal purposes.
        </p>
      </div>
      <p v-else class="my-4 file-delete-modal-text">
        Are you sure you want to delete the selected file?
      </p>

      <template #modal-footer>
        <b-button
          variant="secondary"
          class="file-delete-modal-btn"
          @click="cancelDelFiles()"
        >
          {{ sharedWithCompanies.length ? 'Cancel' : 'No' }}
        </b-button>
        <b-button
          variant="primary"
          class="file-delete-modal-btn"
          @click="deleteFile()"
        >
          {{ sharedWithCompanies.length ? 'Delete File' : 'Yes' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal - Cancel Share -->
    <b-modal
      centered
      v-model="cancelShareModalShow"
      id="cancel-share-modal"
      modal-class="cancel-share-modal"
      title="Cancel this share?"
    >
      <p class="my-2 cancel-share-modal-text">
        {{ shareRecipientEmail }} will lose access to this file.
      </p>

      <template #modal-footer>
        <b-button
          variant="secondary"
          class="cancel-share-modal-btn"
          @click="cancelCancelShare()"
        >
          No
        </b-button>
        <b-button
          variant="primary"
          class="cancel-share-modal-btn"
          @click="cancelShare()"
        >
          Yes
        </b-button>
      </template>
    </b-modal>
  </b-dropdown>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import {
  canDeleteFile as computeCanDeleteFile,
  canViewerEditFile,
  hasBusinessAdminFilePermission,
  isTradieOwnedFile,
} from '@/utils/filePermissions'

export default {
  props: {
    file: {
      type: Object,
      default: null,
    },
    isRequestsFilePage: {
      type: Boolean,
      default: false,
    },
    sharedView: {
      type: Boolean,
      default: false,
    },
    /** When true (with sharedView), Share stays available so a received file can be passed on. */
    canReshare: {
      type: Boolean,
      default: false,
    },
    /** Set only when this card represents one specific share (e.g. a "Shared
     * By Me" card) — the recipient's email, enabling a direct "Cancel Share". */
    shareRecipientEmail: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isPinned: this.file.is_pinned,
      delModalShow: false,
      cancelShareModalShow: false,
    }
  },

  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),

    /**
     * RBAC gating applies to business admins only — tradies aren't governed by
     * the company permission layer, so their actions on their own files stay open.
     */
    isBusinessAdmin() {
      return this.user && this.user.user_type === 'business_admin'
    },

    isSupervisorTradie() {
      return !!(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },

    isTradieOwnedFile() {
      return isTradieOwnedFile(this.file)
    },

    canDownloadFile() {
      return hasBusinessAdminFilePermission(
        this.file,
        this.user,
        this.hasPermission,
        'download',
      )
    },

    hasDeletePermission() {
      return hasBusinessAdminFilePermission(
        this.file,
        this.user,
        this.hasPermission,
        'delete',
      )
    },

    canShareFile() {
      if (!this.isBusinessAdmin) return true
      if (this.isTradieOwnedFile) return false
      return this.hasPermission('files.share')
    },

    /**
     * Reshare permission mirrors the backend's own gate on /file/{file}/reshare
     * (denyIfBusinessAdminLacksSharePermission) — no ownership check, since
     * you don't own this file, you're passing on one shared to you.
     */
    canReshareFile() {
      return !this.isBusinessAdmin || this.hasPermission('files.share')
    },

    // isShareable() {
    //   return (
    //     this.file.status === 'valid' ||
    //     this.file.status === 'not started' ||
    //     this.file.status === 'expiring soon'
    //   )
    // },

    isEditable() {
      return true
    },

    isDeletable() {
      return this.file && this.file.deletable
    },

    canDeleteFile() {
      return computeCanDeleteFile(this.file, this.user)
    },

    // Companies that can see this file; deleting it starts a notice period
    // instead of removing it straight away (API: file.shared_with_companies).
    sharedWithCompanies() {
      return (this.file && this.file.shared_with_companies) || []
    },

    sharedCompanyNames() {
      return this.sharedWithCompanies.map((c) => c.name).join(', ')
    },

    canEditFile() {
      return canViewerEditFile(this.file, this.user, this.hasPermission)
    },

    // ── Per-item visibility ───────────────────────────────────────────────
    // A shared `base` gate plus each item's own rules. Dividers below key off
    // these so a divider only renders between two visible items (never leading).

    ownerIsSubscribed() {
      return !!(this.file && this.file.user && this.file.user.is_subscribed)
    },

    isOwnFile() {
      return !!(this.file && this.file.user && this.file.user.mine)
    },

    showPin() {
      return (
        !this.sharedView &&
        this.ownerIsSubscribed &&
        (!this.isSupervisorTradie || this.isOwnFile)
      )
    },

    showEdit() {
      return (
        !this.sharedView &&
        (!this.isSupervisorTradie || this.isOwnFile) &&
        this.isEditable &&
        this.canEditFile
      )
    },

    showDownload() {
      return !!this.canDownloadFile
    },

    // A tradie's shared file in its 1-week deletion notice period; only the
    // tradie's files ever get deletion_scheduled_at, company files are unaffected.
    isPendingDeletion() {
      return !!(this.file && this.file.deletion_scheduled_at)
    },

    showDelete() {
      return (
        !this.sharedView &&
        !this.isPendingDeletion &&
        (!this.isSupervisorTradie || this.isOwnFile) &&
        this.isDeletable &&
        this.hasDeletePermission
      )
    },

    showShare() {
      if (this.sharedView) {
        return this.canReshare && this.canReshareFile
      }
      return this.canShareFile
    },

    /** Only meaningful for a card that represents one specific share. */
    showCancelShare() {
      return !this.sharedView && !!this.shareRecipientEmail
    },

    /**
     * Whether at least one menu item is available. When false (e.g. a user with
     * view-only access) the whole kebab dropdown is hidden so it isn't an empty
     * menu.
     */
    hasAnyOption() {
      return (
        this.showPin ||
        this.showEdit ||
        this.showDownload ||
        this.showDelete ||
        this.showShare ||
        this.showCancelShare
      )
    },
  },

  methods: {
    ...mapActions({
      getFile: 'file/getFile',
    }),

    async editFile() {
      if (!this.canEditFile) return

      await this.getFile(this.file.id)
      const routeOpts = {
        name: 'file.edit-page',
        params: {
          id: this.file.id,
          isRequestsFilePage: this.isRequestsFilePage,
        },
      }
      if (this.isRequestsFilePage) {
        routeOpts.query = {
          fromRequest: 'true',
          requestFileId: this.file.id,
        }
      }
      this.$router.replace(routeOpts)
    },

    async pinFile() {
      if (this.isPinned) {
        await this.$api.post(`file/${this.file.id}/unpin`).then(({ data }) => {
          if (data.success) {
            this.isPinned = false
            this.$emit('showAlert', 'unpinned')
          }
        })
      } else {
        await this.$api.post(`file/${this.file.id}/pin`).then(({ data }) => {
          if (data.success) {
            this.isPinned = true
            this.$emit('showAlert', 'pinned')
          }
        })
      }
    },
    async deleteFile() {
      if (!this.canDeleteFile || !this.hasDeletePermission) return
      await this.$api.delete(`file/${this.file.id}`).then(({ data }) => {
        if (!data.success) return
        if (data.data && data.data.pending_deletion) {
          // Shared with a company: kept for a week, so refresh rather than
          // navigate away (the file now shows a "pending deletion" state).
          this.deletedFile()
          this.$emit('deletionScheduled', data.data.deletion_scheduled_at)
          this.$emit('showAlert', 'scheduled for deletion')
          return
        }
        this.deletedFile()
        this.$emit('showAlert', 'deleted')
      })
    },
    async downloadFile() {
      if (!this.canDownloadFile) return
      await this.$api
        .get(`download-file/${this.file.id}`, {
          responseType: 'blob',
        })
        .then((data) => {
          const name =
            (this.file.user && this.file.user.full_name) ||
            (this.file.user && this.file.user.email) ||
            'file'
          const filename = `${name} - ${this.file.name}.zip`
          const url = URL.createObjectURL(
            new Blob([data.data], {
              type: 'application/zip',
            }),
          )
          const link = document.createElement('a')

          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()

          this.$emit('showAlert', 'downloaded successfully.')
        })
        .catch((e) => {
          setTimeout((e) => {
            this.$emit('showAlert', 'downloaded unsuccessfully.')
          }, 5000)
        })
    },
    showDelFiles() {
      if (!this.canDeleteFile || !this.hasDeletePermission) return
      this.delModalShow = true
    },
    deletedFile() {
      this.$emit('deletedFile')
      this.cancelDelFiles()
    },
    cancelDelFiles() {
      this.delModalShow = false
    },
    shareFile() {
      if (!this.showShare) return
      this.$emit('shareFile')
    },
    showCancelShareConfirm() {
      if (!this.shareRecipientEmail) return
      this.cancelShareModalShow = true
    },
    cancelCancelShare() {
      this.cancelShareModalShow = false
    },
    async cancelShare() {
      if (!this.shareRecipientEmail) return

      const formData = new FormData()
      formData.append('email', this.shareRecipientEmail)

      await this.$api
        .post(`file/${this.file.id}/share-revoke`, formData)
        .then(() => {
          this.cancelShareModalShow = false
          this.$emit('deletedFile')
          this.$emit('showAlert', 'unshared')
        })
        .catch(() => {
          this.cancelShareModalShow = false
          this.$emit('showAlert', 'unshared unsuccessfully')
        })
    },
  },
}
</script>
<style lang="scss" scoped>
/deep/ .dropdown-menu {
  top: -15px !important;
  left: -20px !important;
}

/deep/ .cancel-share-modal {
  .modal-dialog {
    max-width: 380px;
  }

  .modal-title {
    font-size: 1.15rem;
  }

  .modal-body {
    padding: 0 1rem;
  }
}

.cancel-share-modal-text {
  margin: 0.5rem 0 !important;
  font-size: 0.9rem;
}

.cancel-share-modal-btn {
  height: 44px !important;
  padding: 4px 20px !important;
}

.pin {
  width: 23px;
  height: 23px;
}

// Delete dialog for a file shared with a company: who it's shared with, then
// three short "what happens next" points. Brand blue tint + DESIGN.md grays.
.shared-delete {
  margin: 0.5rem 0 1rem;

  // A company pill and its comma / full stop never separate, so punctuation
  // can't be left stranded on its own line.
  &__item {
    margin-right: 0.35rem;
    white-space: nowrap;
  }

  &__company {
    max-width: 100%;
    padding: 0.4rem 0.8rem;
    background: rgba(0, 108, 255, 0.08);
    color: #006cff;
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1.3;
    text-align: left;
    // Only an unusually long name wraps (inside its own pill).
    white-space: normal;
    word-break: break-word;
  }

  &__text {
    margin: 0 0 0.75rem;
    color: #374151;
    font-size: 0.95rem;
    line-height: 1.5;

    // Same size and font as the paragraph above, just a softer shade -
    // darker than the old gray so it stays readable.
    &--muted {
      margin-bottom: 0;
      color: #4b5563;
    }
  }
}

// Small-phone-only tightening of the delete confirmation modal. Scoped
// inside this media query so nothing changes at 576px and up.
@media (max-width: 575px) {
  /deep/ .file-delete-modal {
    .modal-dialog {
      margin: 0.75rem;
    }

    .modal-title {
      font-size: 1.15rem;
    }

    .modal-body {
      padding: 0 0.5rem;
    }
  }

  .file-delete-modal-text {
    margin: 0.75rem 0 !important;
    font-size: 0.9rem;
  }

  .file-delete-modal-btn {
    height: 44px !important;
    padding: 4px 20px !important;
  }
}
</style>
